import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { extractError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav(loc.state?.from?.pathname || "/dashboard", { replace: true });
    } catch (e) {
      const m = extractError(e);
      setErr(m);
      toast.error(m);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="grid min-h-screen lg:grid-cols-2">
        {/* Left brand panel */}
        <div className="relative hidden overflow-hidden bg-[hsl(var(--primary))] p-12 text-primary-foreground lg:flex lg:flex-col lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-white/10 font-display text-xl font-bold">S</div>
            <span className="font-display text-xl font-bold tracking-tight">Sales CRM</span>
          </div>
          <div className="text-xs text-white/50">© {new Date().getFullYear()} Sales CRM. All rights reserved.</div>
          <div
            aria-hidden
            className="pointer-events-none absolute -right-40 -bottom-40 h-96 w-96 rounded-full bg-white/10 blur-3xl"
          />
        </div>

        {/* Right form */}
        <div className="flex items-center justify-center p-6 sm:p-10">
          <Card className="w-full max-w-md rounded-xl border border-border bg-card p-8 shadow-sm">
            <div className="mb-8">
              <div className="mb-6 flex items-center gap-3 lg:hidden">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground font-display font-bold">S</div>
                <span className="font-display text-lg font-bold tracking-tight">Sales CRM</span>
              </div>
              <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Sign in</h1>
              <p className="mt-1.5 text-sm text-muted-foreground">
                Enter your credentials to access your CRM workspace.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  autoFocus
                  autoComplete="email"
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link
                    to="/forgot-password"
                    data-testid="forgot-password-link"
                    className="text-xs text-primary hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="login-password"
                    type={show ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    aria-label={show ? "Hide password" : "Show password"}
                    onClick={() => setShow((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                  >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {err && (
                <div
                  data-testid="login-error"
                  className="rounded-md border border-[hsl(var(--danger))]/20 bg-[hsl(var(--danger))]/10 px-3 py-2 text-sm text-[hsl(var(--danger))]"
                >
                  {err}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="w-full"
                size="lg"
              >
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Sign in
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
